// Página AdminProducts - Gestión de productos (listado)
// Muestra tabla de productos con acciones CRUD

import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useProducts } from '../../hooks/useFirestore'
import { useCategories } from '../../hooks/useCategories'
import { productsService } from '../../services/firestore.service'
import { getAllStock } from '../../services/stock.service'
import Button from '../../components/Button'
import Toast from '../../components/Toast'
import AddStockModal from '../../components/admin/AddStockModal'
import ImageSlider from '../../components/ImageSlider'
import { formatCurrency } from '../../utils/helpers'
import './AdminProducts.css'

/**
 * Página AdminProducts
 * Listado y gestión de productos
 */
function AdminProducts() {
  const [searchQuery, setSearchQuery] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [notification, setNotification] = useState(null)
  const [productStocks, setProductStocks] = useState({})
  const [viewMode, setViewMode] = useState('cards')
  const [selectedProductForStock, setSelectedProductForStock] = useState(null)
  const [statusFilter, setStatusFilter] = useState('all')
  const [stockFilter, setStockFilter] = useState('all')

  // Cargar preferencia de vista desde localStorage
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const savedView = localStorage.getItem('adminProducts_viewMode')
        if (savedView === 'table' || savedView === 'cards') {
          setViewMode(savedView)
        } else {
          setViewMode('cards')
        }
      } catch (err) {
        console.warn('Error al cargar preferencia de vista:', err)
        setViewMode('cards')
      }
    }
  }, [])

  // Guardar preferencia cuando cambia
  useEffect(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.setItem('adminProducts_viewMode', viewMode)
      } catch (err) {
        console.warn('Error al guardar preferencia de vista:', err)
      }
    }
  }, [viewMode])

  // Obtener productos y categorías
  const { products, loading, error, refetch } = useProducts({})
  const { categories } = useCategories(true) // Incluir inactivas para mostrar todas

  // Cargar stock de todos los productos
  useEffect(() => {
    const loadProductStocks = async () => {
      if (!products || products.length === 0) return
      
      try {
        const stockItems = await getAllStock()
        const totals = stockItems.reduce((acc, item) => {
          const productId = item.productId
          if (!productId) return acc
          acc[productId] = (acc[productId] || 0) + (item.quantity || 0)
          return acc
        }, {})
        const stocks = {}
        products.forEach((product) => {
          stocks[product.id] = totals[product.id] ?? 0
        })
        setProductStocks(stocks)
      } catch (err) {
        console.error('Error cargando stocks:', err)
      }
    }
    
    if (!loading && products.length > 0) {
      const fallbackStocks = {}
      products.forEach((product) => {
        if (Number.isFinite(product.stock)) {
          fallbackStocks[product.id] = product.stock
        }
      })
      if (Object.keys(fallbackStocks).length > 0) {
        setProductStocks(fallbackStocks)
      }
      loadProductStocks()
    }
  }, [products, loading])

  // Función para obtener nombre de categoría
  const getCategoryName = (categoryId) => {
    if (!categoryId) return '-'
    const category = categories.find(cat => cat.id === categoryId)
    return category ? category.name : categoryId
  }

  // Función para clasificar stock
  const getStockCategory = (stock) => {
    if (stock === 0) return 'no-stock'
    if (stock >= 1 && stock <= 5) return 'low-stock'
    return 'in-stock'
  }

  // Filtrar productos por búsqueda, estado y stock
  const filteredProducts = products.filter(product => {
    const stock = productStocks[product.id] ?? 0
    const stockCategory = getStockCategory(stock)

    // Filtro de búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      const matchesSearch =
        (product.name || '').toLowerCase().includes(query) ||
        (product.category || '').toLowerCase().includes(query)
      if (!matchesSearch) return false
    }

    // Filtro de estado
    if (statusFilter !== 'all') {
      const isActive = product.active !== false
      if (statusFilter === 'active' && !isActive) return false
      if (statusFilter === 'inactive' && isActive) return false
    }

    // Filtro de stock
    if (stockFilter !== 'all') {
      if (stockCategory !== stockFilter) return false
    }

    return true
  })

  const resolveImageUrl = (image) => {
    if (!image) return null
    if (typeof image === 'string') return image
    if (typeof image === 'object') {
      return image.url || image.fileId || null
    }
    return null
  }

  const getVariantImage = (product) => {
    const variants = Array.isArray(product?.variants) ? product.variants : []
    for (const variant of variants) {
      const images = Array.isArray(variant?.images) ? variant.images : []
      if (images.length > 0) {
        const resolved = resolveImageUrl(images[0])
        if (resolved) return resolved
      }
    }
    return null
  }

  const getProductImage = (product) => {
    const variantImage = getVariantImage(product)
    if (variantImage) return variantImage
    const images = Array.isArray(product?.images) ? product.images : []
    if (images.length === 0) return null
    return resolveImageUrl(images[0])
  }

  const getProductImages = (product) => {
    const allImages = []
    
    const variants = Array.isArray(product?.variants) ? product.variants : []
    for (const variant of variants) {
      const images = Array.isArray(variant?.images) ? variant.images : []
      for (const img of images) {
        const resolved = resolveImageUrl(img)
        if (resolved && !allImages.includes(resolved)) {
          allImages.push(resolved)
        }
      }
    }
    
    const productImages = Array.isArray(product?.images) ? product.images : []
    for (const img of productImages) {
      const resolved = resolveImageUrl(img)
      if (resolved && !allImages.includes(resolved)) {
        allImages.push(resolved)
      }
    }
    
    return allImages
  }

  // Manejar eliminación de producto
  const handleDelete = async (productId) => {
    try {
      await productsService.delete(productId)
      setDeleteConfirm(null)
      setNotification({
        type: 'success',
        message: 'Producto eliminado correctamente'
      })
      refetch()
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error al eliminar producto:', error)
      setNotification({
        type: 'error',
        message: 'Error al eliminar el producto'
      })
      setTimeout(() => setNotification(null), 5000)
    }
  }

  return (
    <div className={`admin-products ${viewMode === 'cards' ? 'view-cards' : 'view-table'}`}>
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />
      {/* Header con acciones */}
      <div className="admin-products-header">
        <div className="header-left">
          <h2>Gestión de Productos</h2>
          <p>Administra el catálogo de productos</p>
        </div>
        <Link to="/admin/productos/nuevo">
          <Button variant="primary" size="medium">
            ➕ Nuevo Producto
          </Button>
        </Link>
      </div>

      {/* Filtros y búsqueda unificados */}
      <div className="filters-wrapper">
        <div className="filters-container">
          {/* Búsqueda */}
          <div className="search-wrapper">
            <div className="search-container">
              <label htmlFor="product-search" className="search-label">
                <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </label>
              <input
                id="product-search"
                type="text"
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Filtro Estado */}
          <div className="filter-wrapper">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activos</option>
              <option value="inactive">Inactivos</option>
            </select>
          </div>

          {/* Filtro Stock */}
          <div className="filter-wrapper">
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Todo el stock</option>
              <option value="no-stock">Sin stock (0)</option>
              <option value="low-stock">Stock bajo (1-5)</option>
              <option value="in-stock">Con stock (&gt;5)</option>
            </select>
          </div>

          {/* Toggle Vista */}
          <div className="view-toggle">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
            >
              Tabla
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
          </div>
        </div>
      </div>

      {/* Estado de carga */}
      {loading && (
        <div className="loading-state">
          <p>Cargando productos...</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="error-state">
          <p>Error: {error}</p>
          <Button onClick={refetch}>Reintentar</Button>
        </div>
      )}

      {/* Tabla de productos */}
      {!loading && !error && (
        <>
          <div className="products-count">
            Mostrando {filteredProducts.length} de {products.length} producto(s)
            {(statusFilter !== 'all' || stockFilter !== 'all' || searchQuery.trim()) && (
              <button
                className="clear-filters-btn"
                onClick={() => {
                  setSearchQuery('')
                  setStatusFilter('all')
                  setStockFilter('all')
                }}
              >
                Limpiar filtros
              </button>
            )}
          </div>

          {filteredProducts.length > 0 ? (
            <>
              {/* Vista de tabla para desktop */}
              <div className="products-table-container">
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Imagen</th>
                      <th>Nombre</th>
                      <th>Categoría</th>
                      <th>Precio</th>
                      <th>Stock</th>
                      <th>Variantes</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product) => (
                      <tr key={product.id}>
                        <td>
                          {getProductImage(product) ? (
                            <img
                              src={getProductImage(product)}
                              alt={product.name}
                              className="product-thumbnail"
                            />
                          ) : (
                            <div className="product-thumbnail-placeholder">📦</div>
                          )}
                        </td>
                        <td>
                          <strong>{product.name || 'Sin nombre'}</strong>
                        </td>
                        <td>
                          <span className="category-badge">
                            {getCategoryName(product.categoryId || product.category)}
                          </span>
                        </td>
                        <td>
                          <strong>{formatCurrency(product.price || 0)}</strong>
                        </td>
                        <td>{productStocks[product.id] ?? '-'}</td>
                        <td>
                          {product.variants && product.variants.length > 0 ? (
                            <span className="variants-count">{product.variants.length} variante(s)</span>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td>
                          <span className={`status-badge ${product.active !== false ? 'active' : 'inactive'}`}>
                            {product.active !== false ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              className="action-btn stock-btn"
                              onClick={() => setSelectedProductForStock(product)}
                              title="Agregar Stock"
                            >
                              📥
                            </button>
                            <Link
                              to={`/admin/productos/${product.id}`}
                              className="action-btn edit-btn"
                              title="Editar"
                            >
                              ✏️
                            </Link>
                            <button
                              className="action-btn delete-btn"
                              onClick={() => setDeleteConfirm(product.id)}
                              title="Eliminar"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Vista de cards para móviles */}
              <div className="products-cards-container">
                {filteredProducts.map((product) => (
                  <div key={product.id} className="product-card">
                    <div className="product-card-image">
                      <ImageSlider
                        images={getProductImages(product)}
                        alt={product.name}
                      />
                    </div>
                    <div className="product-card-content">
                      <div className="product-card-header">
                        <h3>{product.name || 'Sin nombre'}</h3>
                        <span className={`status-badge ${product.active !== false ? 'active' : 'inactive'}`}>
                          {product.active !== false ? 'Activo' : 'Inactivo'}
                        </span>
                      </div>
                      <div className="product-card-info">
                        <div className="product-info-row">
                          <span className="info-label">Categoría:</span>
                          <span className="category-badge">
                            {getCategoryName(product.categoryId || product.category)}
                          </span>
                        </div>
                        <div className="product-info-row">
                          <span className="info-label">Precio:</span>
                          <strong className="info-value">{formatCurrency(product.price || 0)}</strong>
                        </div>
                        <div className="product-info-row">
                          <span className="info-label">Stock:</span>
                          <span className="info-value">{productStocks[product.id] ?? '-'}</span>
                        </div>
                        {product.variants && product.variants.length > 0 && (
                          <div className="product-info-row">
                            <span className="info-label">Variantes:</span>
                            <span className="variants-count">{product.variants.length} variante(s)</span>
                          </div>
                        )}
                      </div>
                      <div className="product-card-actions">
                        <button
                          className="action-btn stock-btn"
                          onClick={() => setSelectedProductForStock(product)}
                          title="Agregar Stock"
                        >
                          📥 Agregar Stock
                        </button>
                        <Link
                          to={`/admin/productos/${product.id}`}
                          className="action-btn edit-btn"
                          title="Editar"
                        >
                          ✏️ Editar
                        </Link>
                        <button
                          className="action-btn delete-btn"
                          onClick={() => setDeleteConfirm(product.id)}
                          title="Eliminar"
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>No se encontraron productos</p>
              <Link to="/admin/productos/nuevo">
                <Button variant="primary">Crear primer producto</Button>
              </Link>
            </div>
          )}
        </>
      )}

      {/* Modal para agregar stock */}
      <AddStockModal
        isOpen={!!selectedProductForStock}
        onClose={() => setSelectedProductForStock(null)}
        product={selectedProductForStock}
        onStockAdded={() => refetch()}
      />

      {/* Modal de confirmación de eliminación */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>¿Eliminar producto?</h3>
            <p>Esta acción no se puede deshacer. ¿Estás seguro?</p>
            <div className="modal-actions">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancelar
              </Button>
              <Button
                variant="accent"
                onClick={() => handleDelete(deleteConfirm)}
              >
                Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminProducts

